import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';

const values = [
  ['Craft First', 'We obsess over every pixel, every line of code.'],
  ['Client Partnership', 'Your success is our success. Always.'],
  ['Radical Transparency', 'No surprises. Clear timelines, honest pricing.'],
  ['Continuous Learning', 'We stay ahead so your product stays relevant.'],
  ['Impact-Driven', 'We measure success in outcomes, not outputs.'],
  ['Ownership Mindset', "We treat your app like it's our own."],
];

const stats = [
  { value: 25, suffix: '+', label: 'Apps Delivered' },
  { value: 50, suffix: 'K+', label: 'Users Reached' },
  { value: 95, suffix: '%', label: 'Client Satisfaction' },
  { value: 5, suffix: '+', label: 'Years of Expertise' },
];

const cultureItems = [
  [
    'Remote First',
    'Our team spans 3 continents. We work async-first with structured check-ins — great work has no timezone.',
    'M12 21s7-4.5 7-11a7 7 0 1 0-14 0c0 6.5 7 11 7 11Zm0-8a3 3 0 1 0 0-6 3 3 0 0 0 0 6Z',
  ],
  [
    'Agile by Default',
    'Two-week sprints, weekly client demos, continuous delivery. You always know exactly where things stand.',
    'M4 12a8 8 0 0 1 13.7-5.6M20 12a8 8 0 0 1-13.7 5.6M17 3v3.4h-3.4M7 21v-3.4h3.4',
  ],
  [
    'Client as Co-Creator',
    "We don't disappear behind a wall of tickets. You're in the Slack, in the Figma, in the loop — always.",
    'M8 12h8M7 8h.01M17 8h.01M6 16c1.4 1.3 3.4 2 6 2s4.6-.7 6-2M5 5h14v14H5V5Z',
  ],
];

function useCountUp(targets) {
  const sectionRef = useRef(null);
  const [counts, setCounts] = useState(targets.map(() => 0));

  useEffect(() => {
    const section = sectionRef.current;

    if (!section) {
      return undefined;
    }

    let animationFrame;
    let hasAnimated = false;

    const animate = () => {
      const start = performance.now();
      const duration = 1600;

      const tick = (time) => {
        const progress = Math.min((time - start) / duration, 1);
        const eased = 1 - Math.pow(1 - progress, 3);

        setCounts(targets.map((target) => Math.round(target.value * eased)));

        if (progress < 1) {
          animationFrame = requestAnimationFrame(tick);
        }
      };

      animationFrame = requestAnimationFrame(tick);
    };

    if (!('IntersectionObserver' in window)) {
      animate();
      return () => cancelAnimationFrame(animationFrame);
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !hasAnimated) {
          hasAnimated = true;
          animate();
          observer.disconnect();
        }
      },
      { threshold: 0.35 },
    );

    observer.observe(section);

    return () => {
      cancelAnimationFrame(animationFrame);
      observer.disconnect();
    };
  }, [targets]);

  return { sectionRef, counts };
}

function AboutPage() {
  const { sectionRef, counts } = useCountUp(stats);

  return (
    <main className="about-page">
      <section className="about-hero" aria-labelledby="about-hero-title">
        <div className="about-hero__inner">
          <p className="about-breadcrumb">
            <Link to="/">Home</Link> / About
          </p>
          <p className="hero-chip">✦ Our Story</p>
          <h1 id="about-hero-title" className="about-hero__title">
            <span>We Are Milestor.</span>
            <span>We Build the Future,</span>
            <span>One App at a Time.</span>
          </h1>
          <p>
            Born from a passion for great software and a belief that every idea deserves exceptional
            execution.
          </p>
        </div>
      </section>

      <section className="about-story" aria-labelledby="story-title">
        <div className="about-story__inner">
          <div className="about-story__copy reveal">
            <span className="about-label">OUR STORY</span>
            <h2 id="story-title">From a Small Team with Big Dreams</h2>
            <p>
              Placeholder narrative: Milestor began as a small group of product obsessives who
              believed mobile apps could be more thoughtful, more useful, and more beautifully made.
            </p>
            <p>
              Placeholder narrative: Our first clients trusted us with ambitious ideas, and each
              launch taught us how to pair strategy, design, and engineering with genuine care.
            </p>
            <p>
              Placeholder narrative: Today, we support growing companies and global teams with
              award-minded work, transparent collaboration, and a relentless focus on outcomes.
            </p>
            <blockquote>We don't just write code — we build products people love.</blockquote>
          </div>
          <div className="about-story__visual reveal" aria-hidden="true">
            <span className="story-letter">M</span>
            <span className="story-chip story-chip--one">25+ Apps</span>
            <span className="story-chip story-chip--two">50K+ Users</span>
            <span className="story-chip story-chip--three">5+ Years</span>
          </div>
        </div>
      </section>

      <section className="about-purpose" aria-labelledby="purpose-title">
        <div className="about-section-heading reveal">
          <span className="about-label">WHAT DRIVES US</span>
          <h2 id="purpose-title">Our Mission, Vision & Values</h2>
        </div>
        <div className="mission-grid">
          <article className="mission-card reveal">
            <svg viewBox="0 0 24 24" aria-hidden="true">
              <path d="M12 3v3m0 12v3M3 12h3m12 0h3M7 12a5 5 0 1 0 10 0 5 5 0 0 0-10 0Zm3 0a2 2 0 1 0 4 0 2 2 0 0 0-4 0Z" />
            </svg>
            <h3>Our Mission</h3>
            <p>
              To empower businesses of all sizes with mobile experiences that drive real-world impact
              — beautifully designed, expertly engineered.
            </p>
          </article>
          <article className="mission-card reveal">
            <svg viewBox="0 0 24 24" aria-hidden="true">
              <path d="M3 12s3.5-6 9-6 9 6 9 6-3.5 6-9 6-9-6-9-6Zm6 0a3 3 0 1 0 6 0 3 3 0 0 0-6 0Z" />
            </svg>
            <h3>Our Vision</h3>
            <p>
              A world where every great idea has access to world-class mobile technology — regardless
              of company size or budget.
            </p>
          </article>
        </div>
        <div className="values-grid">
          {values.map(([title, text], index) => (
            <article className="value-card reveal" key={title} style={{ '--reveal-delay': `${index * 70}ms` }}>
              <span>{String(index + 1).padStart(2, '0')}</span>
              <h3>{title}</h3>
              <p>{text}</p>
            </article>
          ))}
        </div>
      </section>

      <section className="about-stats" ref={sectionRef} aria-labelledby="about-stats-title">
        <h2 id="about-stats-title" className="visually-hidden">
          Milestor milestones
        </h2>
        <div className="stats-grid">
          {stats.map((stat, index) => (
            <div className="stat-item" key={stat.label}>
              <strong>
                {counts[index]}
                {stat.suffix}
              </strong>
              <span>{stat.label}</span>
            </div>
          ))}
        </div>
        <p className="about-stats__quote reveal">
          “The best apps feel inevitable — simple, useful, and crafted with care.”
        </p>
      </section>

      <section className="culture-section" id="culture" aria-labelledby="culture-title">
        <div className="about-section-heading reveal">
          <span className="about-label">HOW WE WORK</span>
          <h2 id="culture-title">Remote-First. Results-Driven. Human Always.</h2>
        </div>
        <div className="culture-grid">
          {cultureItems.map(([title, text, icon], index) => (
            <article className="culture-card reveal" key={title} style={{ '--reveal-delay': `${index * 90}ms` }}>
              <svg viewBox="0 0 24 24" aria-hidden="true">
                <path d={icon} />
              </svg>
              <h3>{title}</h3>
              <p>{text}</p>
            </article>
          ))}
        </div>
      </section>

      <section className="cta-banner" aria-labelledby="about-cta-title">
        <div className="cta-banner__inner reveal">
          <h2 id="about-cta-title">Want to Work With a Team That Truly Cares?</h2>
          <p>We'd love to hear about your project.</p>
          <div className="cta-banner__actions">
            <Link className="button button--light" to="/contact">
              Start a Project
            </Link>
            <a className="button button--ghost" href="#culture">
              Meet the Team <span aria-hidden="true">→</span>
            </a>
          </div>
        </div>
      </section>
    </main>
  );
}

export default AboutPage;
